import { api } from '@/config/api';

export type DocumentType = 'quotation' | 'invoice' | 'proposal' | 'note' | 'contract' | 'other';
export type DocumentStatus = 'draft' | 'sent' | 'viewed' | 'accepted' | 'declined' | 'expired';

export interface DocumentLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRatePct?: number;
  lineTotal?: number;
}

export interface CrmDocument {
  _id: string;
  documentUid: string;
  companyUid: string;
  contactUid?: string;
  type: DocumentType;
  status: DocumentStatus;
  title: string;
  body: string;
  summary?: string;
  amount?: number;
  currency?: string;
  validUntil?: string;
  lineItems?: DocumentLineItem[];
  // Tax + invoice lifecycle
  taxRatePct?: number;
  taxAmount?: number;
  dueDate?: string;
  paidAt?: string;
  paidAmount?: number;
  paymentMethod?: string;
  paymentRef?: string;
  convertedFromUid?: string;
  // Stripe payment link (only on invoices, only after generation)
  stripePaymentLinkUrl?: string;
  stripePaymentLinkId?: string;
  stripePaymentLinkAt?: string;
  createdByUserUid: string;
  aiGenerated: boolean;
  isTemplate?: boolean;
  clonedFromUid?: string;
  sentAt?: string;
  viewedAt?: string;
  respondedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CashflowBucketTotal {
  /** In `CashflowSnapshot.currency`, converted at today's rate. */
  total: number;
  count: number;
  /** Currencies that couldn't be converted (no rate) — left out of `total`. */
  unconverted: string[];
}

export interface CashflowSnapshot {
  /** One row per currency, in that currency. */
  paid: Array<{ _id: string; total: number; count: number }>;
  outstanding: Array<{ _id: string; total: number; count: number }>;
  overdue: Array<{ _id: string; total: number; count: number }>;
  /** The company's currency. */
  currency?: string;
  totals?: { paid: CashflowBucketTotal; outstanding: CashflowBucketTotal; overdue: CashflowBucketTotal };
  ratesAsOf?: string | null;
}

export interface VatReport {
  range: { from?: string; to?: string };
  rows: Array<{
    taxRatePct: number | null;
    currency: string;
    net: number;
    tax: number;
    gross: number;
    count: number;
  }>;
}

export interface Letterhead {
  companyName: string;
  companyDomain: string;
  logoUrl: string;
  tagline: string;
  industry: string;
}

export interface ListDocumentsResponse {
  items: CrmDocument[];
  total: number;
  page: number;
  limit: number;
}

const BASE = '/slighter_crm/documents';

export const documentsService = {
  list: async (params: {
    q?: string; type?: DocumentType; status?: DocumentStatus; contactUid?: string;
    page?: number; limit?: number;
  } = {}): Promise<ListDocumentsResponse> =>
    (await api.get(BASE, { params })).data,

  get: async (uid: string): Promise<CrmDocument> =>
    (await api.get(`${BASE}/${uid}`)).data,

  create: async (payload: {
    type: DocumentType; title: string; body?: string; summary?: string;
    contactUid?: string; amount?: number; currency?: string; validUntil?: string;
    taxRatePct?: number; vatInclusive?: boolean;
    lineItems?: DocumentLineItem[];
  }): Promise<CrmDocument> =>
    (await api.post(BASE, payload)).data,

  update: async (uid: string, payload: Partial<{
    title: string; body: string; summary: string;
    contactUid: string; amount: number; currency: string; validUntil: string;
    taxRatePct: number; vatInclusive: boolean;
    lineItems: DocumentLineItem[];
  }>): Promise<CrmDocument> =>
    (await api.patch(`${BASE}/${uid}`, payload)).data,

  markSent: async (uid: string): Promise<CrmDocument> =>
    (await api.post(`${BASE}/${uid}/mark-sent`)).data,

  sendWhatsapp: async (uid: string, payload: { phone: string; messagePrefix?: string; accountUid?: string }): Promise<{ ok: boolean; waMessageId?: string; error?: string; url: string }> =>
    (await api.post(`${BASE}/${uid}/send-whatsapp`, payload)).data,

  sendEmailPrep: async (uid: string, payload: { email: string; subject?: string; bodyPrefix?: string }): Promise<{ mailto: string; subject: string; body: string; url: string }> =>
    (await api.post(`${BASE}/${uid}/send-email-prep`, payload)).data,

  aiLineItems: async (uid: string, payload: { brief: string; provider?: string; model?: string }): Promise<{ lineItems: Array<{ description: string; quantity: number; unitPrice: number; taxRatePct?: number }>; tokensIn: number; tokensOut: number }> =>
    (await api.post(`${BASE}/${uid}/ai-line-items`, payload)).data,

  remove: async (uid: string): Promise<{ ok: boolean }> =>
    (await api.delete(`${BASE}/${uid}`)).data,

  letterhead: async (): Promise<Letterhead> =>
    (await api.get(`${BASE}/letterhead`)).data,

  aiAssist: async (uid: string, payload: {
    prompt: string; provider?: string; model?: string; context?: string[];
  }): Promise<{ text: string; tokensIn: number; tokensOut: number }> =>
    (await api.post(`${BASE}/${uid}/ai-assist`, payload)).data,

  // ─── Templates ─────────────────────────────────────────────────────────────

  listTemplates: async (type?: DocumentType): Promise<{ items: CrmDocument[]; total: number }> =>
    (await api.get(`${BASE}/templates`, { params: { type } })).data,

  cloneFromTemplate: async (
    templateUid: string,
    opts: { contactUid?: string; titleSuffix?: string } = {},
  ): Promise<CrmDocument> =>
    (await api.post(`${BASE}/templates/${templateUid}/clone`, opts)).data,

  markAsTemplate: async (uid: string, isTemplate: boolean): Promise<CrmDocument> =>
    (await api.post(`${BASE}/${uid}/mark-template`, { isTemplate })).data,

  /** URL the browser opens in a new tab — render.html responds with the printable view. */
  renderHtmlUrl: (uid: string): string => {
    const base = (process.env.REACT_APP_API_BASE ?? '').replace(/\/$/, '');
    return `${base}${BASE}/${uid}/render.html`;
  },

  // ─── Accounting-lite ──────────────────────────────────────────────────────

  markPaid: async (uid: string, body: { paidAt?: string; paidAmount?: number; paymentMethod?: string; paymentRef?: string } = {}): Promise<CrmDocument> =>
    (await api.post(`${BASE}/${uid}/mark-paid`, body)).data,

  markUnpaid: async (uid: string): Promise<CrmDocument> =>
    (await api.post(`${BASE}/${uid}/mark-unpaid`)).data,

  /**
   * Generate (or reuse) a Stripe Checkout URL the customer can pay through.
   * Pass `regenerate: true` to mint a fresh session.
   */
  paymentLink: async (uid: string, opts: { regenerate?: boolean } = {}): Promise<{ url: string; sessionId: string }> =>
    (await api.post(`${BASE}/${uid}/payment-link`, undefined, { params: opts.regenerate ? { regenerate: '1' } : {} })).data,

  convertToInvoice: async (uid: string): Promise<CrmDocument> =>
    (await api.post(`${BASE}/${uid}/convert-to-invoice`)).data,

  cashflow: async (filter: { from?: string; to?: string } = {}): Promise<CashflowSnapshot> =>
    (await api.get(`${BASE}/financials/cashflow`, { params: filter })).data,

  vatReport: async (filter: { from?: string; to?: string } = {}): Promise<VatReport> =>
    (await api.get(`${BASE}/financials/vat-report`, { params: filter })).data,
};
